import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { styled } from '@uhg-abyss/web/tools/styled';
import { TourGuide } from '@sde/widgets-core/src/common/TourGuide';
import { useWindowSize } from '@sde/widgets-core/src/tools/useWindowSize';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { useTabs } from '@sde/widgets-core/src/redux/resources/tabs';
import { validatePermissions } from '@sde/widgets-core/src/redux/resources/user/helpers';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useModal } from 'src/state/modal';

const Container = styled('div', {
  padding: '10px',
});

const SecondaryHeader = styled('div', {
  paddingBottom: '8px',
  fontSize: '18px',
});

const SecondLine = styled('div', {
  display: 'flex',
  marginBottom: 15,

  '&:last-child': {
    marginBottom: 0,
  },
});

const SecondText = styled('div', {
  fontSize: '14px',
  lineHeight: '20px',
  paddingRight: 10,
});

const TrainingTip = (tipData) => {
  const { header = null, text = null } = tipData;

  return (
    <Container>
      <If condition={header}>
        <SecondaryHeader>
          <strong>{header}</strong>
        </SecondaryHeader>
      </If>
      <SecondLine>
        {header ? (
          <SecondText>{text}</SecondText>
        ) : (
          <SecondaryHeader>{text}</SecondaryHeader>
        )}
      </SecondLine>
    </Container>
  );
};

export const OnboardingTour = ({ isActive, onClose }) => {
  const { width } = useWindowSize();
  let steps = [];
  const startIndex = getFeature('TABBED_ACTION_REQUIRED_WIDGET') ? 1 : 0;
  const { setTabIndex } = useTabs.actions();
  const currentTabIndex = useTabs.state().tabIndex;

  const { visibleTabs } = useTabs.state();
  const { user } = useUser.state();

  const appPermissions = {
    claims: validatePermissions(user, 'claims.read'),
    eligibility: validatePermissions(user, 'eligibility.read'),
    referrals: validatePermissions(user, 'referrals.read'),
    paan: validatePermissions(user, 'prior-auth.read'),
  };

  const [isClaimsTabEnabled, setIsClaimsTabEnabled] = useState(false);
  const [isSmartEditsTabEnabled, setIsSmartEditsTabEnabled] = useState(false);
  const [isPaanTabEnabled, setIsPaanTabEnabled] = useState(false);

  useEffect(() => {
    setIsClaimsTabEnabled(
      appPermissions.claims && getFeature('CLAIMS_TRACKIT')
    );
  }, [appPermissions.claims, getFeature('CLAIMS_TRACKIT')]);

  useEffect(() => {
    setIsSmartEditsTabEnabled(
      appPermissions.claims &&
        getFeature('CLAIMS_TRACKIT') &&
        getFeature('TRACKIT_SMARTEDITS_TAB')
    );
  }, [
    appPermissions.claims,
    getFeature('CLAIMS_TRACKIT'),
    getFeature('TRACKIT_SMARTEDITS_TAB'),
  ]);

  useEffect(() => {
    setIsPaanTabEnabled(appPermissions.priorAuth && getFeature('TRACKIT_PAAN'));
  }, [appPermissions.priorAuth, getFeature('TRACKIT_PAAN')]);

  const incrementTab = () => {
    setTabIndex(currentTabIndex + 1);
  };

  const { toggleModal } = useModal.actions();
  const setIsCustomizeFlyoutOpen = (isOpen) => {
    toggleModal({ key: 'customizeHomePage', isOpen });
  };
  const openCustomizeFlyout = () => {
    setIsCustomizeFlyoutOpen(true);
  };

  const aboveHeader = {
    options: {
      zIndex: 1600,
    },
  };
  const belowHeader = {
    options: {
      zIndex: 1070,
    },
  };

  const globalNavigation = {
    target: '[data-testid="nav-menu-wrapper"]',
    modalName: 'Global Navigation',
    content: TrainingTip({
      header: 'You can jump in from up here.',
      text:
        width > 984
          ? 'This is your main navigation bar. Click on any topic to jump to that section. If a topic name has a "v" next to it, click for links and descriptions for other pages.'
          : 'Click this to access your main navigation menu. Click on any topic to jump to that section. If a topic name has a "v" next to it, click for links and descriptions for other pages.',
    }),
    styles: aboveHeader,
  };
  const payerProvider = {
    target: '[data-testid="payer-provider-wrapper"]',
    modalName: 'Payer & Provider Menus',
    content: TrainingTip({
      text: 'Use the Payer and Provider menus to switch the information used in all the searches and tasks you perform.',
    }),
    styles: aboveHeader,
  };
  const registerTin = {
    target: '[data-testid="register-tin-wrapper"]',
    modalName: 'Register Tin',
    content: TrainingTip({
      text: 'Find the tools you need with the search field. Keep in mind that using some tools requires registration. ',
    }),
    styles: belowHeader,
  };
  const trackitBar = {
    target: '[data-testid="trackit-bar-wrapper"]',
    modalName: 'TrackIt Bar',
    content: TrainingTip({
      text: `The Action Required bar is your list of "to-do's" that require action. Click any item on the list to get the details.`,
    }),
    styles: belowHeader,
  };
  const tools = {
    target: '[data-testid="fiveTab-tools-wrapper"]',
    modalName: 'Tools',
    content: TrainingTip({
      text: `Here you'll find the tools available for you to use.`,
    }),
    styles: belowHeader,
  };

  const fiveTabStepsContent = {
    eligibility: {
      name: 'eligibility',
      modalName: 'Eligibility',
      permissions: appPermissions.eligibility,
      target:
        width > 1292
          ? '[data-testid="right-rail-wrapper"]'
          : '[data-testid="right-rail-collapsed-wrapper"]',
      content: TrainingTip({
        text: 'Find shortcuts to relevant eligibility resources, links and tools. Click through to see your most recent eligibility searches.',
      }),
    },
    claims: {
      name: 'claims',
      modalName: 'Claims & Payments',
      permissions: appPermissions.claims,
      target:
        width > 1292
          ? '[data-testid="right-rail-wrapper"]'
          : '[data-testid="right-rail-collapsed-wrapper"]',
      content: TrainingTip({
        text: 'Find shortcuts to relevant claims resources, links and tools. Click through to see any claims you may have flagged.',
      }),
    },
    priorAuth: {
      name: 'priorAuth',
      modalName: 'Prior Authorizations & Notifications',
      permissions: true,
      target:
        width > 1292
          ? '[data-testid="right-rail-wrapper"]'
          : '[data-testid="right-rail-collapsed-wrapper"]',
      content: TrainingTip({
        text: 'Find shortcuts to prior authorization relevant resources, links and tools. ',
      }),
    },
    referrals: {
      name: 'referrals',
      modalName: 'Referrals',
      permissions: appPermissions.referrals,
      target:
        width > 1292
          ? '[data-testid="right-rail-wrapper"]'
          : '[data-testid="right-rail-collapsed-wrapper"]',
      content: TrainingTip({
        text: 'Find shortcuts to relevant referral resources and links.',
      }),
    },
    doclib: {
      name: 'doclib',
      modalName: 'Documents & Reporting',
      permissions: true,
      target:
        width > 1292
          ? '[data-testid="right-rail-wrapper"]'
          : '[data-testid="right-rail-collapsed-wrapper"]',
      content: TrainingTip({
        text: 'Find shortcuts to relevant document and reporting resources, links and tools. ',
      }),
    },
    uhcUpdates: {
      name: 'uhcUpdates',
      modalName: 'UnitedHealthcare Updates',
      permissions: true,
      toggle: 'PROVIDER_PORTAL_UPDATES',
      target: '[data-testid="fiveTab-component-wrapper"]',
      content: TrainingTip({
        text: `Stay in the loop with UnitedHealthcare's provider portal updates.`,
      }),
    },
  };

  const customizeButton = {
    target: '[data-testid="customize-button-wrapper"]',
    modalName: 'Customize',
    content: TrainingTip({
      header: 'Now, you can customize your personal home page.',
      text: `Add or remove what you see in the  main tools menu by checking or un-checking the boxes for each item. Selections will be saved so you'll see your customized page the next time you log in.`,
    }),
    styles: belowHeader,
    onNext: openCustomizeFlyout,
  };
  const customizeFlyout = {
    target: '[data-testid="drag-and-drop-wrapper"]',
    modalName: 'Customize',
    content: TrainingTip({
      text: 'You can change the order of the tools by simply clicking and dragging items.',
    }),
    styles: aboveHeader,
    placement: 'bottom',
    // onNext: closeCustomizeFlyout,
  };
  const centralForm = {
    target: '[data-testid="fiveTab-wrapper"]',
    modalName: 'Customize',
    content: TrainingTip({
      text: `Now you can review documentation and take action in your preferred tools. You'll also see important UnitedHealthcare updates.`,
    }),
    styles: belowHeader,
  };

  const featureToggleCheck = (tab) => {
    return tab.toggle ? getFeature(tab.toggle) : true;
  };
  const setFiveTabSteps = () => {
    if (visibleTabs && visibleTabs.length > 0) {
      const filteredFiveTabSteps = visibleTabs
        .map((tab) => {
          return {
            styles: belowHeader,
            onNext: incrementTab,
            ...fiveTabStepsContent[tab],
          };
        })
        .filter((mappedTab) =>
          mappedTab?.permissions === true
            ? featureToggleCheck(mappedTab)
            : false
        );
      if (filteredFiveTabSteps.length === 1) {
        filteredFiveTabSteps[0].target = '[data-testid="fiveTab-wrapper"]';
      }
      return filteredFiveTabSteps;
    }
    return [{}];
  };

  const setSteps = () => {
    steps = [globalNavigation, payerProvider];
    steps.push(registerTin);
    if (
      getFeature('TRACKIT_ACTION_REQUIRED_HOME_PAGE') &&
      (isClaimsTabEnabled || isSmartEditsTabEnabled || isPaanTabEnabled)
    )
      steps.push(trackitBar);

    const fiveTabSteps = setFiveTabSteps();
    if (fiveTabSteps.length > 1) steps.push(tools);
    steps.push(...fiveTabSteps);
    steps.push(...[customizeButton, customizeFlyout, centralForm]);
  };

  useEffect(() => {
    setSteps();
  }, [
    visibleTabs,
    isClaimsTabEnabled,
    isSmartEditsTabEnabled,
    isPaanTabEnabled,
  ]);

  setSteps();

  return (
    <TourGuide
      isActive={isActive}
      scrollOffset={310}
      onClose={() => {
        setTabIndex(0);
        onClose();
      }}
      onStart={() => {
        // actionRequired is index of 0. Need to update startIndex to 1 if AR toggle is active until training tip is added for actionRequired tab
        setTabIndex(startIndex);
      }}
      steps={steps}
      disableScrolling
      floaterProps={{
        styles: { floater: { pointerEvents: 'auto', zIndex: 2500 } },
      }}
    />
  );
};

OnboardingTour.propTypes = {
  isActive: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

OnboardingTour.defaultProps = {
  onClose: () => {},
};
